import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { GoogleLogo } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || null;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Signed in as ${u.name}`);
      if (next) return nav(next);
      if (u.role === "admin") nav("/admin");
      else if (u.role === "affiliate") nav("/affiliate");
      else nav("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="page-enter mx-auto max-w-md px-6 py-24">
      <div className="overline mb-3">Account</div>
      <h1 className="font-display text-5xl leading-none">Sign in.</h1>

      <button
        type="button"
        onClick={startGoogle}
        className="mt-8 w-full inline-flex items-center justify-center gap-3 px-5 py-3.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-zinc-200 transition-colors"
        data-testid="login-google"
      >
        <GoogleLogo size={18} weight="bold" />
        Continue with Google
      </button>

      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-white/10" />
        <div className="overline">or email</div>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <label className="block">
          <div className="overline mb-1">Email</div>
          <input
            type="email"
            value={email}
            required
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-white/15 px-3 py-3 outline-none focus:border-blue-500 bg-white/[0.03] text-sm rounded-lg"
            data-testid="login-email"
          />
        </label>
        <label className="block">
          <div className="overline mb-1">Password</div>
          <input
            type="password"
            value={password}
            required
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-white/15 px-3 py-3 outline-none focus:border-blue-500 bg-white/[0.03] text-sm rounded-lg"
            data-testid="login-password"
          />
        </label>
        {error && <div className="text-sm text-red-500" data-testid="login-error">{error}</div>}
        <button className="btn-prm w-full" disabled={loading} data-testid="login-submit">
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <div className="mt-6 text-sm text-zinc-500">
        No account?{" "}
        <Link to="/register" className="link-underline" data-testid="login-to-register">Create one</Link>
      </div>
    </div>
  );
}
